/**
 * 学员端应用壳
 * -----------------------------------------------------------------------------
 * 路由与页面在 T-18 落地（口径见 `docs/05-ui/01-页面清单与路由.md` §1，共 11 页）。
 *
 * 两条全局约定：
 *   - 落地即解析 `?entry=` 并记住入口码，之后所有 `/api/h5/*` 请求都会带上 `x-entry`
 *   - **除 `/guide` 外**所有页面都套 `RequireSession`：渲染前先确认会话，
 *     防止转发链接直接看到业务数据（口径 C-01）
 */
import { type ReactNode, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { consumeEntryFromUrl, resolveEntryCode } from './api/client';
import { CameraEnvProbe } from './components/CameraEnvProbe';
import { RequireProfile } from './components/RequireProfile';
import { RequireSession } from './components/RequireSession';
import CertificateHelp from './pages/CertificateHelp';
import EnrollForm from './pages/EnrollForm';
import EnrollNew from './pages/EnrollNew';
import EnrollSuccess from './pages/EnrollSuccess';
import Guide from './pages/Guide';
import Help from './pages/Help';
import Home from './pages/Home';
import Inbox from './pages/Inbox';
import { InvoiceApplyDetail, InvoiceApplyList } from './pages/InvoiceApply';
import LogisticsList from './pages/LogisticsList';
import LogisticsTrack from './pages/LogisticsTrack';
import Me from './pages/Me';
import NotFoundPage from './pages/NotFound';
import PaymentGuide from './pages/PaymentGuide';
import PaymentGuideEntry from './pages/PaymentGuideEntry';
import ProfileComplete from './pages/ProfileComplete';
import RegistrationDetail from './pages/RegistrationDetail';
import RegistrationList from './pages/RegistrationList';
import ServiceHelp from './pages/ServiceHelp';

/**
 * 入口码引导：落地链接上的 `?entry=` 会被记住，然后**立刻从地址栏抹掉**。
 *
 * 两步顺序不能颠倒：
 *   ① `consumeEntryFromUrl()` 先写进 `sessionStorage`（后续所有请求靠它带 `x-entry`）
 *   ② 再把 `entry` 从查询串里删掉
 *
 * 为什么要抹：`?entry=menu01` 是**投放渠道的记账字段**，不是给学员看的东西。
 * 留在地址栏上有三个副作用 —— 学员看到会觉得「这链接不正经」；复制转发时把记账码
 * 一起带出去，别人拿到就等于拿到一个可用入口；截图外发时同样泄漏。抹掉之后
 * 地址栏是干净的 `https://<域名>/home`，而 `sessionStorage` 里仍然带着完整归因。
 *
 * 抹掉前必须确认「已经落得住」：`resolveEntryCode()` 回读一致才动地址栏。
 * 隐私模式下 `sessionStorage` 会抛异常（回读为 `null`），此时宁可不抹 ——
 * 抹掉而没存住，用户一刷新就再也读不到入口码，等于把归因彻底丢掉。
 *
 * 刷新后仍在：抹掉的是**地址栏**，不是归因。同一会话内 `sessionStorage` 还在，
 * 服务端会话 Cookie 也还在，刷新不影响主链路；只有「会话过期 + 地址栏已无 entry」
 * 同时发生才会落引导页 —— 这正是设计意图（见 `RequireSession` 头注释）。
 */
function EntryBootstrap() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const entry = consumeEntryFromUrl(location.search);
    if (!entry) return;

    // 没落住就保留 URL 上的码，宁可露着也不能丢
    if (resolveEntryCode() !== entry) return;

    const params = new URLSearchParams(location.search);
    params.delete('entry');
    const rest = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: rest ? `?${rest}` : '',
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}

/** 需要会话的页面统一在这里包一层，避免每个页面各写一遍 */
function Guarded({ children }: { children: ReactNode }) {
  return (
    <RequireSession>
      {/*
        第二层门：账号级资料（姓名 + 手机号）没填完的一律拦到 /profile/complete。
        放在 RequireSession **内层** —— 先确认「你是谁」，再判「资料齐没齐」。
        白名单在 `RequireProfile` 里（填写页自身 + 联系客服）。
      */}
      <RequireProfile>{children}</RequireProfile>
    </RequireSession>
  );
}

export function App() {
  return (
    <>
      <EntryBootstrap />
      {/* 相机环境自检：只在 `?camera-debug=1` 时渲染，正常安装包里永远不出现 */}
      <CameraEnvProbe />
      <Routes>
        {/* 公开页：不带身份凭证 */}
        <Route path="/guide" element={<Guide />} />

        {/*
          资料强制填写页。只套会话门、**不套 `Guarded`** —— 套了就会
          「资料未完成 → 重定向到自己 → 再判未完成 → 再重定向」，死循环。
        */}
        <Route
          path="/profile/complete"
          element={
            <RequireSession>
              <ProfileComplete />
            </RequireSession>
          }
        />

        <Route
          path="/home"
          element={
            <Guarded>
              <Home />
            </Guarded>
          }
        />

        {/* 服务台宫格入口（静态路由先行；与 seed 的入口注册表 route 一一对应） */}
        <Route
          path="/enroll/new"
          element={
            <Guarded>
              <EnrollNew />
            </Guarded>
          }
        />
        <Route
          path="/registration/list"
          element={
            <Guarded>
              <RegistrationList />
            </Guarded>
          }
        />
        <Route
          path="/payment/guide"
          element={
            <Guarded>
              <PaymentGuideEntry />
            </Guarded>
          }
        />
        {/* 教材领取：列表（按报名单）→ 单张领取页。**一个报名单领一次**，
            所以必须是两级路由，不能像原来那样一进页面就落到「最近一张单」 */}
        <Route
          path="/logistics/track"
          element={
            <Guarded>
              <LogisticsList />
            </Guarded>
          }
        />
        <Route
          path="/logistics/:registrationId"
          element={
            <Guarded>
              <LogisticsTrack />
            </Guarded>
          }
        />
        {/* 申请发票：列表（按报名单选单）→ 申请面板。开票是报名单维度操作，列表即选择器 */}
        <Route
          path="/invoice"
          element={
            <Guarded>
              <InvoiceApplyList />
            </Guarded>
          }
        />
        <Route
          path="/invoice/:registrationId"
          element={
            <Guarded>
              <InvoiceApplyDetail />
            </Guarded>
          }
        />
        <Route
          path="/help/certificate"
          element={
            <Guarded>
              <CertificateHelp />
            </Guarded>
          }
        />
        <Route
          path="/help/service"
          element={
            <Guarded>
              <ServiceHelp />
            </Guarded>
          }
        />

        <Route
          path="/enroll/success"
          element={
            <Guarded>
              <EnrollSuccess />
            </Guarded>
          }
        />
        <Route
          path="/enroll/:registrationId"
          element={
            <Guarded>
              <EnrollForm />
            </Guarded>
          }
        />
        <Route
          path="/payment/:registrationId"
          element={
            <Guarded>
              <PaymentGuide />
            </Guarded>
          }
        />
        <Route
          path="/registration/:registrationId"
          element={
            <Guarded>
              <RegistrationDetail />
            </Guarded>
          }
        />
        <Route
          path="/inbox"
          element={
            <Guarded>
              <Inbox />
            </Guarded>
          }
        />
        <Route
          path="/me"
          element={
            <Guarded>
              <Me />
            </Guarded>
          }
        />
        <Route
          path="/help"
          element={
            <Guarded>
              <Help />
            </Guarded>
          }
        />

        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
